import { useEffect, useState } from 'react';
import { OrgLogin } from './pages/OrgLogin';
import { OrgDashboard } from './pages/OrgDashboard';
import { ResetPassword } from './pages/ResetPassword';
import { ResubscribePage, PlanExpiredNotice } from './pages/ResubscribePage';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import {
  DEFAULT_THEME, ROLES, isPlanExpired, clearAllStoreData,
} from './data/store';
import { useNotification } from './hooks/useNotification';
import { Toast } from './components/Toast';
import { GlobalStyles } from './components/GlobalStyles';
import { fetchWithAuth } from './utils/api';

// Organization web application — org admins and members.
// localStorage keys are namespaced with "org." so this app and the
// Super Admin app can run side-by-side in the same browser.

const OrgShell = () => {
  const { setTheme } = useTheme();
  const [isLoaded, setIsLoaded] = useState(false);
  const [view, setView] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname === '/reset-password') {
      return 'reset-password';
    }
    return localStorage.getItem('org.view') || 'login';
  });
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('org.user'));
      if (u) {
        if (u.role === 'Warehouse Admin') u.role = 'warehouse-admin';
        if (u.role === 'Warehouse User') u.role = 'warehouse-user';
        if (u.role === 'Organizer') u.role = 'org-admin';
        if (u.role === 'Branch Admin') u.role = 'branch-admin';
        if (u.role === 'Branch User') u.role = 'org-user';
      }
      return u || null;
    } catch { return null; }
  });
  const [currentOrg, setCurrentOrg] = useState(() => {
    try { return JSON.parse(localStorage.getItem('org.org')) || null; } catch { return null; }
  });
  const urlParams = new URLSearchParams(window.location.search);
  const isTvDisplay = urlParams.get('tv') === 'true';

  const [activeTab, setActiveTab] = useState(() => isTvDisplay ? 'kiosk-queue' : (localStorage.getItem('org.activeTab') || 'dashboard'));
  const { notification, showNotification, dismiss } = useNotification();

  const role = currentUser?.role || null;

  // Nav layout preference is stored on the user record (DB column `sidebar`):
  // true → fixed sidebar, false → draggable floating tab. New users default to
  // the sidebar, so logging in for the first time shows the fixed sidebar.
  const navMode = currentUser?.sidebar === false ? 'floating' : 'sidebar';
  const setNavMode = (mode) => {
    const sidebar = mode === 'sidebar';
    setCurrentUser((prev) => (prev ? { ...prev, sidebar } : prev));
    fetchWithAuth('/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sidebar }),
    }).catch((e) => console.error('Failed to persist nav preference:', e));
  };

  useEffect(() => {
    if (view !== 'reset-password') {
      localStorage.setItem('org.view', view);
    }
  }, [view]);
  useEffect(() => {
    if (!isTvDisplay) {
      localStorage.setItem('org.activeTab', activeTab);
    }
  }, [activeTab, isTvDisplay]);
  useEffect(() => {
    if (currentUser) {
      // Exclude large fields like avatarUrl (if it's base64) to prevent quota exceeded errors
      const { organization, branch, warehouse, avatarUrl, ...safeUser } = currentUser;
      try {
        localStorage.setItem('org.user', JSON.stringify(safeUser));
      } catch (e) {
        console.error('Failed to save user to localStorage, automatically clearing data to prevent crashes:', e);
        // Automatically wipe local storage so the user NEVER has to manually clear it
        localStorage.clear();
      }
    } else {
      localStorage.removeItem('org.user');
    }
  }, [currentUser]);
  useEffect(() => {
    if (currentOrg) {
      try {
        const {
          receiptLogo,
          gstProof,
          panProof,
          fssaiProof,
          tradeLicenseProof,
          additionalProofs,
          ...safeOrg
        } = currentOrg;
        localStorage.setItem('org.org', JSON.stringify(safeOrg));
      } catch (e) {
        console.warn('Failed to save org to localStorage:', e);
      }
    } else {
      localStorage.removeItem('org.org');
    }
  }, [currentOrg]);
  useEffect(() => { setIsLoaded(true); }, []);

  // Proactively check expiry and force logout if they are past the grace period.
  useEffect(() => {
    if (!currentOrg || !currentOrg.planExpiresAt) return;
    const now = Date.now();
    const expiry = new Date(currentOrg.planExpiresAt).getTime();
    const GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
    if (now > expiry + GRACE_PERIOD_MS && role !== ROLES.ORG_ADMIN) {
      handleLogout();
    }
  }, [currentOrg, role]);

  // After hydrating from localStorage, fetch the org's latest record so the
  // expiry check below reflects current server state — otherwise a stored
  // session that pre-dates an admin-side expiry change would slip through.
  useEffect(() => {
    if (!currentOrg?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/organizations');
        if (cancelled || !res.ok) return;
        const list = await res.json();
        const fresh = list.find((o) => o.id === currentOrg.id);
        if (!fresh) return;
        let modulesObj = {};
        try {
          if (typeof fresh.modules === 'string') modulesObj = JSON.parse(fresh.modules);
          else if (fresh.modules && typeof fresh.modules === 'object') modulesObj = fresh.modules;
        } catch { /* keep empty */ }
        setCurrentOrg((prev) => ({
          ...prev,
          ...fresh,
          modules: modulesObj,
          // Keep the locally-resolved theme so primary colors don't flicker.
          theme: prev?.theme || {
            primary: fresh.themePrimary || DEFAULT_THEME.primary,
            secondary: fresh.themeSecondary || DEFAULT_THEME.secondary,
            tertiary: fresh.themeTertiary || DEFAULT_THEME.tertiary,
          },
        }));
      } catch (e) {
        console.error('Failed to refresh org on mount:', e);
      }
    })();
    return () => { cancelled = true; };
    // Run once on mount per session — re-running on every currentOrg change
    // would loop because we call setCurrentOrg inside.

  }, []);

  // Fetch fresh profile on mount to get the avatarUrl (which is stripped from localStorage)
  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth('/profile');
        if (cancelled || !res.ok) return;
        const freshUser = await res.json();
        setCurrentUser((prev) => prev ? { ...prev, ...freshUser, role: prev.role } : null);
      } catch (e) {
        console.error('Failed to refresh user on mount:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Theme follows the signed-in organization.
  useEffect(() => {
    setTheme(currentOrg ? currentOrg.theme : DEFAULT_THEME);
  }, [currentOrg, setTheme]);

  // Guard: dashboard needs both a user and an org.
  useEffect(() => {
    if (view === 'dashboard' && (!currentUser || !currentOrg)) setView('login');
  }, [view, currentUser, currentOrg]);

  const handleLogin = (user, org) => {
    if (user.accessToken) {
      localStorage.setItem('org.accessToken', user.accessToken);
      localStorage.setItem('org.refreshToken', user.refreshToken);
    }
    setCurrentUser(user);
    setCurrentOrg(org);
    setActiveTab('dashboard');
    setView('dashboard');
    showNotification(`Welcome to ${org.name}`, 'success');
  };

  function handleLogout() {
    clearAllStoreData(); // wipe all in-memory tenant data before switching org
    setCurrentUser(null);
    setCurrentOrg(null);
    localStorage.removeItem('org.accessToken');
    localStorage.removeItem('org.refreshToken');
    setView('login');
    setTheme(DEFAULT_THEME);
  }

  const roleLabel = role === ROLES.ORG_ADMIN
    ? 'Organization Admin'
    : role === ROLES.BRANCH_ADMIN
      ? 'Branch Admin'
      : role === ROLES.WAREHOUSE_ADMIN
        ? 'Warehouse Admin'
        : role === ROLES.WAREHOUSE_USER
          ? 'Warehouse User'
          : 'Branch User';
  const dashboardUser = currentUser
    ? {
      ...currentUser,
      role: roleLabel, // TopBar and Profile rely on this display string
    }
    : null;

  const handleUpdateUser = (updatedUser) => {
    setCurrentUser((prev) => prev ? { ...prev, ...updatedUser, role: prev.role } : null);
  };

  return (
    <div className="w-full font-sans overflow-hidden select-none" style={{ minHeight: '100dvh', height: '100dvh', background: 'var(--theme-secondary)' }}>
      {view === 'reset-password' && (
        <ResetPassword showNotification={showNotification} />
      )}
      {view === 'login' && (
        <OrgLogin
          onLogin={handleLogin}
          showNotification={showNotification}
          isLoaded={isLoaded}
          applyTheme={setTheme}
          hideBack
        />
      )}
      {view === 'dashboard' && currentUser && currentOrg && (role === ROLES.ORG_ADMIN || role === ROLES.BRANCH_ADMIN || role === ROLES.WAREHOUSE_ADMIN || role === ROLES.WAREHOUSE_USER || role === ROLES.ORG_USER) && (
        isPlanExpired(currentOrg)
          ? (role === ROLES.ORG_ADMIN
            ? (
              <ResubscribePage
                org={currentOrg}
                onLogout={handleLogout}
                showNotification={showNotification}
                onRenewed={(updated) => setCurrentOrg({ ...currentOrg, ...updated, theme: currentOrg.theme })}
              />
            )
            : <PlanExpiredNotice org={currentOrg} onLogout={handleLogout} />
          )
          : (
            <OrgDashboard
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              handleLogout={handleLogout}
              showNotification={showNotification}
              navMode={navMode}
              setNavMode={setNavMode}
              currentUser={dashboardUser}
              currentOrg={currentOrg}
              role={role}
              onUpdateUser={handleUpdateUser}
              onUpdateOrg={(updated) => setCurrentOrg((prev) => ({ ...prev, ...updated, theme: prev?.theme }))}
            />
          )
      )}

      <Toast notification={notification} onClose={dismiss} />
      <GlobalStyles />
    </div>
  );
};

export const OrgApp = () => {
  // Hydrate the initial theme from the saved org so refreshes feel seamless.
  const savedOrg = typeof window !== 'undefined' ? (
    (() => { try { return JSON.parse(localStorage.getItem('org.org')); } catch { return null; } })()
  ) : null;
  return (
    <ThemeProvider initialTheme={savedOrg?.theme || DEFAULT_THEME}>
      <OrgShell />
    </ThemeProvider>
  );
};

export default OrgApp;
